import React, { useEffect, useMemo } from 'react';
import { Card, CardBody, CardHeader as _CardHeader, CardTitle } from '../components/NewDesign/Card';
import { HelpLink } from '../components/NewDesign/HelpLink';
import { getTranslationKey } from '../modules/utils';
import { Alert } from '../components/NewDesign/Alert';
import { Spacer } from '../components/NewDesign/Spacer';
import { useDispatch, useSelector } from 'react-redux';
import { getPayoutAccountSelector, getModalsStates } from '../modules/selectors';
import {
  fetchPaymentInfoAction,
  fetchPayoutAccountAction,
  fetchShopPayinPaymentMethod,
  changeModalState,
  createGlobalShopSettings,
  updateGlobalShopSettings
} from '../modules/actions';
import { InvoiceDownloadErrorModal } from '../components/InvoiceDownloadErrorModal';
import { Tabs, SwitchItem } from '../components';
import { useGlobalShopSettings } from '../hooks/shop/useGlobalShopSettings';
import { BillingAddressTab } from './ShopPaymentSettings/BillingAddressTab';
import { PayinPaymentMethodTab } from './ShopPaymentSettings/PayinPaymentMethodTab';
import { useIsSetup } from '../components/Stripe/StripePaymentElement';
import { PayoutMethodsTab } from './ShopPaymentSettings/PayoutMethodsTab';
import { isBillingAddressFilled } from '../common/is-billing-address-filled';
import { isTaxConfirmed } from '../common/is-tax-confirmed';
import { getPaymentInfo } from '../modules/selectors/paymentInfo';
import { useUserHasPayinPaymentMethod } from './ShopPaymentSettings/common';
import { getUser } from '../modules/selectors/user';
import styled from 'styled-components';
import { useLocation } from 'react-router';
import qs from 'qs';
import { DocumentsTab } from './ShopPaymentSettings/DocumentsTab';
import { TaxSettingsTab } from './ShopPaymentSettings/TaxSettingsTab';
// import { Link } from 'react-router-dom';
import media from '../assets/css/media';

const CardHeader = styled(_CardHeader)`
  display: flex;
  width: 100%;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
`;

// const StyledLink = styled(Link)`
//   display: inline-block;
//   margin-left: 5px;
// `;

const ParagraphContainer = styled.p`
  width: 60%;
  @media all and (max-width: ${media.mobile}) {
    width: 100%;
  }
`;

// const shouldShowDisclaimerAlert = () => {
//   const now = new Date();
//   const novemberMonth = 10;
//   const novemberFirst = new Date(now.getFullYear(), novemberMonth, 1);

//   return now < novemberFirst;
// };

export function useTabFromURL() {
  const location = useLocation();
  const searchParams = qs.parse(location.search.substring(1));

  return searchParams.tab as string;
}

export const ShopPaymentSettings = () => {
  const dispatch = useDispatch();
  const payoutAccount = useSelector(getPayoutAccountSelector);
  const { address: billingAddress, userId, shopPayinPaymentMethod } = useSelector(getPaymentInfo);
  const { taxSettings } = useSelector(getUser);
  const modalsState = useSelector(getModalsStates);

  const { globalShopSettings, isGlobalShopActive } = useGlobalShopSettings();

  const changeShopActiveCallback = (value: boolean) => {
    if (globalShopSettings) {
      dispatch(updateGlobalShopSettings({ id: globalShopSettings.id, printShopActive: value }));
    } else {
      dispatch(createGlobalShopSettings({ printShopActive: value }));
    }
  };

  const userHasPaymentMethod = useUserHasPayinPaymentMethod();
  const isPayoutMethodProvided =
    (payoutAccount.ibanEnabled && payoutAccount.iban && payoutAccount.accountHolder) ||
    (payoutAccount.payPalEnabled && payoutAccount.payPalClientId && payoutAccount.payPalSecret);
  const dataIsReady = !!userId && payoutAccount.loaded && shopPayinPaymentMethod.loaded;
  // disable alert for initial release
  const showAlert =
    false &&
    dataIsReady &&
    !(isBillingAddressFilled(billingAddress) && isPayoutMethodProvided && userHasPaymentMethod);
  const isSetup = useIsSetup();
  const tabFromUrl = useTabFromURL();
  let defaultActiveTab = 'billing-address';
  if (isSetup) {
    defaultActiveTab = 'payin-payment-method';
  } else if (tabFromUrl) {
    defaultActiveTab = tabFromUrl;
  }

  const billingAddressWarning = dataIsReady && !isBillingAddressFilled(billingAddress);
  const taxSettingsWarning = dataIsReady && !isTaxConfirmed(taxSettings);
  const settlementAccountWarning = dataIsReady && !userHasPaymentMethod;

  const tabs = useMemo(
    () => [
      {
        id: 'billing-address',
        label: () => getTranslationKey('shop.paymentSettings.billingAddress'),
        warning: billingAddressWarning,
        component: () => (
          <CardBody padded className="animated fadeIn">
            <BillingAddressTab />
          </CardBody>
        )
      },
      {
        id: 'tax-settings',
        label: () => getTranslationKey('settingsSection.taxSettingMenuLabel'),
        warning: taxSettingsWarning,
        component: () => (
          <CardBody padded className="animated fadeIn">
            <TaxSettingsTab />
          </CardBody>
        )
      },
      {
        id: 'payin-payment-method',
        label: () => getTranslationKey('shop.paymentSettings.payinPaymentMethod'),
        warning: settlementAccountWarning,
        component: () => (
          <CardBody padded className="animated fadeIn">
            <PayinPaymentMethodTab />
          </CardBody>
        )
      },
      {
        id: 'payout-methods',
        label: () => getTranslationKey('shop.paymentSettings.payoutMethods'),
        component: () => (
          <CardBody padded className="animated fadeIn">
            <PayoutMethodsTab />
          </CardBody>
        )
      },
      {
        id: 'documents',
        label: () => getTranslationKey('shop.paymentSettings.documents.title'),
        component: () => (
          <CardBody padded className="animated fadeIn">
            <DocumentsTab />
          </CardBody>
        )
      }
    ],
    [billingAddressWarning, taxSettingsWarning, settlementAccountWarning]
  );

  useEffect(() => {
    dispatch(fetchPayoutAccountAction());
    dispatch(fetchShopPayinPaymentMethod());
    dispatch(fetchPaymentInfoAction());
  }, []);

  return (
    <div>
      <Card padded={false}>
        <CardHeader padded className="mb-0">
          <CardTitle>
            {getTranslationKey('shop.paymentSettings.pageTitle')}
            <HelpLink href={getTranslationKey('shop.paymentSettings.helpLink')}>Get Help</HelpLink>
          </CardTitle>
          <ParagraphContainer
            className="mt-4"
            dangerouslySetInnerHTML={{ __html: getTranslationKey('shop.paymentSettings.intro') }}
          />
          <Spacer mid />
          <Alert
            // TODO: wait for payout account do load if there is one
            isOpen={showAlert}
            dismissible={false}
            color="danger"
            className="animated fadeIn"
          >
            {getTranslationKey('shop.paymentSettings.alert')}
          </Alert>
          <div data-cy="global-shop-toggle">
            <SwitchItem
              id="global-shop-active"
              value={isGlobalShopActive}
              changeCallback={changeShopActiveCallback}
              label={getTranslationKey('collectionShop.toggleLabel')}
              direction="row-reverse"
              withTooltip
              isIconTooltip
              tooltipText={getTranslationKey('shop.paymentSettings.shopActiveHint')}
            />
          </div>
        </CardHeader>
        <Tabs tabs={tabs} newDesign defaultActiveTab={defaultActiveTab} />
      </Card>
      <InvoiceDownloadErrorModal
        isOpen={modalsState.invoiceDownloadErrorModal}
        closeCallback={() =>
          dispatch(changeModalState({ key: 'invoiceDownloadErrorModal', state: false }))
        }
      />
    </div>
  );
};
